import type { FocusEventHandler, KeyboardEventHandler } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { dealLockHooks, useTermNotSupported } from '../../../hooks';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { selectWithRetry, useSelectOnFocusEnabled } from '@makemydeal/dr-shared-components/select-on-focus';

import { GENERIC_LAST_FIELD_CHANGED_ERROR, TERM } from '../../../constants';
import { addValueSuffix, getTermNotSupportedMessage, parseSelectedTermMonths, removeValueSuffix } from '../utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { kebabCase } from 'lodash';
import { BALLOON, FINANCE } from '@makemydeal/dr-platform-types';
import type { SelectTermOptions } from '@makemydeal/dr-offer-redux';

const FINANCE_TERM_OVERRIDE_INDICATOR_TEST_ID = 'finance-term-override-indicator';

export const useFinanceTermField = ({ isSummaryTab }: { isSummaryTab?: boolean }) => {
    const dispatch = useDispatch();
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isImprovedAutoSelectEnabled = useSelector(featureToggleSelectors.isImprovedAutoSelectEnabled);
    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);
    const isTermFieldDownPaymentTermIdEnabled = useSelector(featureToggleSelectors.isTermFieldDownPaymentTermIdEnabled);
    const useNewTermIdStandard = useSelector(featureToggleSelectors.useNewTermIdStandard);
    const isAutoSelectEnabled = useSelectOnFocusEnabled();
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const selectedTermIdByOfferType = useSelector(offerReduxSelectors.getTermIdByOfferType);
    const financeTermId = useSelector((state: StateTree) => offerReduxSelectors.getTermIdByOfferType(state, FINANCE));
    const failed = useSelector(compositeSelectors.getLastFailureActionIfError(offerActionTypes.SELECTED_TERM));
    const isPaymentGridEnabled = useSelector((state: StateTree) => offerReduxSelectors.getPaymentGridToggle(state, offerType));
    const downPayment = useSelector((state: StateTree) => offerReduxSelectors.getDownPaymentByOfferType(state, offerType)) ?? 0;
    const initialPaymentDiscount = useSelector((state: StateTree) => offerReduxSelectors.getInitialPaymentDiscount(state));
    const isTermNotSupported = useTermNotSupported();
    const isFocused = useRef(false);
    const isLocked = dealLockHooks.useIsDealLocked();
    const selectedTermId = offerType === BALLOON && !selectedTermIdByOfferType ? financeTermId : selectedTermIdByOfferType;

    // grab the current, formatted value
    const formattedTerm = useMemo((): string => {
        const months = parseSelectedTermMonths(selectedTermId);
        return addValueSuffix(months);
    }, [selectedTermId]);

    const [termNumber, setTermNumber] = useState(formattedTerm);

    // Redux holds the term the user asked for, because dash does not revert a failed SELECTED_TERM. It does
    // not hold the term that was replaced, so this field stores that, tagged with the term it applies to.
    // Hide the generic error only when the indicator shows this message in its place.
    const [replacedTerm, setReplacedTerm] = useState<{ forTermMonths: number; previousMonths?: number }>();
    const currentTermMonths = parseSelectedTermMonths(selectedTermId);
    const previousTermMonths =
        replacedTerm && replacedTerm.forTermMonths === currentTermMonths ? replacedTerm.previousMonths : undefined;
    const termNotSupportedMessage = isTermNotSupported ? getTermNotSupportedMessage(currentTermMonths, previousTermMonths) : '';

    const hasBlockingFailure = failed && !termNotSupportedMessage;
    const failedMessage = hasBlockingFailure ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined;

    useEffect(() => {
        if (!isFocused.current && termNumber !== formattedTerm) {
            setTermNumber(formattedTerm);
        }
    }, [formattedTerm, termNumber]);

    // Update local state when redux value for the term get updated
    useEffect(() => {
        if (isDealCentralExperience) {
            if (isFocused.current) {
                setTermNumber(removeValueSuffix(formattedTerm));
            } else {
                setTermNumber(formattedTerm);
            }
        }
    }, [selectedTermId, isDealCentralExperience, formattedTerm]);

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled && isSummaryTab) {
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.SUMMARY, kebabCase(TERM), hasBlockingFailure));
        }
    }, [isDealScreenRedDotErrorsEnabled, dispatch, hasBlockingFailure, isSummaryTab]);

    const updateSelectedTerm = (termId: string, options: SelectTermOptions = {}) =>
        dispatch(
            offerReduxActionCreators.selectedTerm(termId, {
                payment: true,
                suppressAnalytics: true,
                ...options
            })
        );

    const handleInputChanged = (e: any) => {
        const newValue = e.target.value;
        const newValueNumber = Number(e.target.value);
        const prevTerm = parseSelectedTermMonths(selectedTermId);
        if (newValue === termNumber || newValue.length > 3 || isNaN(newValueNumber)) {
            return;
        }

        dispatch(offerActionCreators.updatePaymentCallPendingState(prevTerm !== newValueNumber));

        setTermNumber(newValueNumber.toString());
    };

    const handleInputFocus: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
        isFocused.current = true;
        const target = e.currentTarget;
        const stripped = removeValueSuffix(termNumber);
        setTermNumber(stripped);
        if (isAutoSelectEnabled) selectWithRetry(target, stripped);
    };

    const validateAndUpdateTerm = () => {
        const prevTerm = parseSelectedTermMonths(selectedTermId);
        const termValue = Number(termNumber.replace('Months', '').trim());
        if (prevTerm !== termValue) {
            const termIdExtension = {
                DOWNPAYMENT: downPayment,
                ...(useNewTermIdStandard && initialPaymentDiscount ? { FORDFLEXBUY: initialPaymentDiscount } : {})
            };
            const id = isTermFieldDownPaymentTermIdEnabled
                ? offerReduxSelectors.getTermId(offerType, termValue, termIdExtension)
                : offerReduxSelectors.getTermId(offerType, termValue);

            if (id) {
                // After a failure redux holds the failed term. Do not record it as the previous value.
                if (isImprovedAutoSelectEnabled && !isTermNotSupported) {
                    setReplacedTerm({ forTermMonths: termValue, previousMonths: prevTerm });
                }
                // For balloon deals, include the custom term value in the payment request override
                if (offerType === BALLOON && !selectedTermIdByOfferType) {
                    updateSelectedTerm(id, {
                        meta: {
                            paymentRequestOverride: {
                                balloon: {
                                    termMonths: termValue,
                                    termList: [termValue],
                                    downPayment
                                }
                            }
                        }
                    });
                } else {
                    updateSelectedTerm(id);
                }
            }
            dispatch(interactionAnalyticsActionCreators.financeTermDropdown());
        }
        setTermNumber(addValueSuffix(termValue));
    };

    const handleInputBlur = () => {
        validateAndUpdateTerm();
        isFocused.current = false;
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
        if (e.key === 'Enter') {
            validateAndUpdateTerm();
        }
    };

    return {
        label: isSummaryTab ? `${TERM} (Monthly)` : TERM,
        overrideIndicatorProps: {
            enabled: isImprovedAutoSelectEnabled,
            show: Boolean(termNotSupportedMessage),
            message: termNotSupportedMessage,
            testId: FINANCE_TERM_OVERRIDE_INDICATOR_TEST_ID,
            announce: isExplicitLenderP1Enabled
        },
        inputProps: {
            'data-testid': 'CMDTermInput',
            name: TERM,
            hasError: hasBlockingFailure,
            errorMessage: failedMessage,
            onChange: handleInputChanged,
            onFocus: handleInputFocus,
            onBlur: handleInputBlur,
            onKeyDown: handleKeyDown,
            value: termNumber,
            size: 'sm' as const,
            readOnly: isPaymentGridEnabled,
            disabled: isLocked
        }
    };
};
